import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { Button } from './Button';
import { XIcon, ShoppingCartIcon, PlusIcon, MinusIcon, TrashIcon, ArrowLeftIcon, CheckIcon, DumbbellIcon, UtensilsIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TrainingPlanWarningModal } from './TrainingPlanWarningModal';
import { trackInitiateCheckout } from '../utils/analytics';
interface CartProps {
  returnPath?: string;
}
export const Cart: React.FC<CartProps> = ({
  returnPath = '/bhs'
}) => {
  const {
    items,
    removeItem,
    updateQuantity,
    clearCart,
    itemCount,
    discountInfo,
    applyDiscount,
    removeDiscount,
    trainingPlanWarningOpen,
    setTrainingPlanWarningOpen,
    existingTrainingPlanName
  } = useCart();
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [showCouponField, setShowCouponField] = useState(false);
  const [couponCode, setCouponCode] = useState(discountInfo.couponCode);
  const [couponApplied, setCouponApplied] = useState(discountInfo.couponApplied);
  const [couponError, setCouponError] = useState('');
  const [selectedPlanForDiscount, setSelectedPlanForDiscount] = useState<string | null>(discountInfo.selectedPlanForDiscount);
  const [discountAmount, setDiscountAmount] = useState(discountInfo.discountAmount);
  const [isClearing, setIsClearing] = useState(false);
  const [upsellChecked, setUpsellChecked] = useState(false);
  // Check if cart has a BHS training plan
  const hasBhsPlan = items.some(item => item.id === 'training-beginner-1' || item.id === 'training-beginner-3');
  // Reset upsell if BHS plan is removed
  useEffect(() => {
    if (!hasBhsPlan) {
      setUpsellChecked(false);
    }
  }, [hasBhsPlan]);
  const UPSELL_PRICE = 399;
  // Check if user came from training landing page
  const cameFromTrainingLanding = sessionStorage.getItem('cameFromTrainingLanding') === 'true';
  // Přidání useEffect, který zajistí, že po načtení stránky se okno posune nahoru
  useEffect(() => {
    window.scrollTo(0, 0);
    // Načtení stavu slevy z kontextu
    if (discountInfo.couponApplied) {
      setCouponApplied(discountInfo.couponApplied);
      setCouponCode(discountInfo.couponCode);
      setSelectedPlanForDiscount(discountInfo.selectedPlanForDiscount);
      setDiscountAmount(discountInfo.discountAmount);
      setShowCouponField(true);
    }
  }, [discountInfo]);
  // Funkce pro získání tréninkových plánů z košíku
  const getTrainingPlans = () => {
    return items.filter(item => item.type === 'training' && item.details?.includes('3 měsíce'));
  };
  // Funkce pro výpočet slevy pro daný plán
  const calculateDiscount = (planName: string) => {
    if (planName.includes('ZAČÁTEČNÍK')) {
      return 179.9;
    } else if (planName.includes('POKROČILÝ')) {
      return 189.9;
    } else if (planName.includes('PROFESIONÁL')) {
      return 199.9;
    }
    return 0;
  };
  // Funkce pro aplikaci kupónu
  const applyCoupon = () => {
    setCouponError('');
    if (couponCode.trim().toUpperCase() !== 'SLEVA10') {
      setCouponError('Neplatný kupónový kód.');
      return;
    }
    const trainingPlans = getTrainingPlans();
    if (trainingPlans.length === 0) {
      setCouponError('V košíku není žádný tréninkový plán na 3 měsíce.');
      return;
    }
    if (trainingPlans.length === 1) {
      // Pokud je v košíku pouze jeden tréninkový plán, automaticky ho vybereme
      const newSelectedPlan = trainingPlans[0].id;
      setSelectedPlanForDiscount(newSelectedPlan);
      const discount = calculateDiscount(trainingPlans[0].name);
      setDiscountAmount(discount);
      setCouponApplied(true);
      // Uložení informací o slevě do kontextu
      applyDiscount({
        couponCode: couponCode,
        discountAmount: discount,
        selectedPlanForDiscount: newSelectedPlan,
        couponApplied: true
      });
    } else if (!selectedPlanForDiscount) {
      // Pokud je více plánů a žádný není vybrán, zobrazíme chybu
      setCouponError('Vyberte prosím tréninkový plán, na který chcete uplatnit slevu.');
      return;
    } else {
      // Pokud je vybrán plán, vypočítáme slevu
      const selectedPlan = items.find(item => item.id === selectedPlanForDiscount);
      if (selectedPlan) {
        const discount = calculateDiscount(selectedPlan.name);
        setDiscountAmount(discount);
        setCouponApplied(true);
        // Uložení informací o slevě do kontextu
        applyDiscount({
          couponCode: couponCode,
          discountAmount: discount,
          selectedPlanForDiscount: selectedPlanForDiscount,
          couponApplied: true
        });
      }
    }
  };
  // Funkce pro odstranění kupónu
  const removeCoupon = () => {
    setCouponApplied(false);
    setCouponCode('');
    setSelectedPlanForDiscount(null);
    setDiscountAmount(0);
    removeDiscount();
  };
  // Funkce pro změnu vybraného plánu pro slevu
  const handleSelectPlanForDiscount = (planId: string) => {
    setSelectedPlanForDiscount(planId);
    // Pokud je již kupón aplikován, aktualizujeme slevu
    if (couponApplied) {
      const selectedPlan = items.find(item => item.id === planId);
      if (selectedPlan) {
        const discount = calculateDiscount(selectedPlan.name);
        setDiscountAmount(discount);
        // Aktualizace informací o slevě v kontextu
        applyDiscount({
          couponCode: couponCode,
          discountAmount: discount,
          selectedPlanForDiscount: planId,
          couponApplied: true
        });
      }
    }
  };
  const handleCheckout = () => {
    // Track InitiateCheckout event before redirecting to Stripe
    trackInitiateCheckout({
      items: items.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        category: item.type
      })),
      totalValue: total
    });
    // Debug logging
    console.log('Checkout clicked:', {
      itemsCount: items.length,
      firstItemId: items[0]?.id,
      firstItemName: items[0]?.name,
      upsellChecked
    });
    // BHS SYSTÉM 1 měsíc + upsell
    if (items.length === 1 && items[0].id === 'training-beginner-1' && upsellChecked) {
      console.log('Redirecting to BHS 1 měsíc + průvodce výživou');
      setIsRedirecting(true);
      setTimeout(() => {
        window.location.href = 'https://buy.stripe.com/28E14mgYX0jw9IF2vAg7e09';
      }, 1500);
    }
    // BHS SYSTÉM 3 měsíce + upsell
    else if (items.length === 1 && items[0].id === 'training-beginner-3' && upsellChecked) {
      console.log('Redirecting to BHS 3 měsíce + průvodce výživou');
      setIsRedirecting(true);
      setTimeout(() => {
        window.location.href = 'https://buy.stripe.com/3cI28q387feq2gd5HMg7e0a';
      }, 1500);
    }
    // Zkontroluj, zda je v košíku pouze plán ZAČÁTEČNÍK (1 měsíc)
    else if (items.length === 1 && items[0].id === 'training-beginner-1') {
      console.log('Redirecting to ZAČÁTEČNÍK 1 měsíc');
      setIsRedirecting(true);
      setTimeout(() => {
        window.location.href = 'https://buy.stripe.com/7sY5kCeQP7LYdYVb26g7e01';
      }, 1500);
    }
    // Zkontroluj, zda je v košíku pouze plán ZAČÁTEČNÍK (3 měsíce)
    else if (items.length === 1 && items[0].id === 'training-beginner-3') {
      console.log('Redirecting to ZAČÁTEČNÍK 3 měsíce');
      setIsRedirecting(true);
      setTimeout(() => {
        window.location.href = 'https://buy.stripe.com/7sYdR87onc2ecUR1rwg7e02';
      }, 1500);
    }
    // Zkontroluj, zda je v košíku pouze plán POKROČILÝ (1 měsíc)
    else if (items.length === 1 && items[0].id === 'training-intermediate-1') {
      console.log('Redirecting to POKROČILÝ 1 měsíc');
      setIsRedirecting(true);
      setTimeout(() => {
        window.location.href = 'https://buy.stripe.com/fZu8wOcIHaYacUR9Y2g7e04';
      }, 1500);
    }
    // Zkontroluj, zda je v košíku pouze plán POKROČILÝ (3 měsíce)
    else if (items.length === 1 && items[0].id === 'training-intermediate-3') {
      console.log('Redirecting to POKROČILÝ 3 měsíce');
      setIsRedirecting(true);
      setTimeout(() => {
        window.location.href = 'https://buy.stripe.com/6oU3cu7on1nA5sp4DIg7e05';
      }, 1500);
    }
    // Zkontroluj, zda je v košíku pouze plán PROFESIONÁL (1 měsíc)
    else if (items.length === 1 && items[0].id === 'training-advanced-1') {
      console.log('Redirecting to PROFESIONÁL 1 měsíc');
      setIsRedirecting(true);
      setTimeout(() => {
        window.location.href = 'https://buy.stripe.com/4gM7sKeQP6HU4ol6LQg7e06';
      }, 1500);
    }
    // Zkontroluj, zda je v košíku pouze plán PROFESIONÁL (3 měsíce)
    else if (items.length === 1 && items[0].id === 'training-advanced-3') {
      console.log('Redirecting to PROFESIONÁL 3 měsíce');
      setIsRedirecting(true);
      setTimeout(() => {
        window.location.href = 'https://buy.stripe.com/dRm9ASbED1nAf2Z7PUg7e07';
      }, 1500);
    }
    // Pokud je v košíku více produktů nebo neznámý produkt
    else {
      console.log('Multiple items or unknown product in cart');
      alert('V košíku můžete mít pouze jeden tréninkový plán. Prosím odstraňte ostatní produkty.');
    }
  };
  const handleClearCart = () => {
    setIsClearing(true);
    setTimeout(() => {
      clearCart();
      setIsClearing(false);
    }, 400);
  };
  // Handle "Continue Shopping" button
  const handleContinueShopping = () => {
    navigate(returnPath);
    setTimeout(() => window.scrollTo(0, 0), 100);
  };
  if (items.length === 0) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} transition={{
      duration: 0.4,
      ease: 'easeInOut'
    }} className="min-h-screen relative bg-black text-white pt-20 overflow-hidden" data-id="element-76">
        {/* Gradient background overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-green-900/40 via-green-900/20 to-black pointer-events-none z-0" data-id="element-77"></div>
        {/* Decorative elements */}
        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-green-500/3 rounded-full filter blur-3xl" data-id="element-78"></div>
        <div className="absolute -bottom-10 right-10 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-79"></div>
        <div className="absolute top-1/2 left-1/4 w-40 h-40 bg-green-500/3 rounded-full filter blur-3xl" data-id="element-80"></div>
        <div className="container mx-auto px-4 py-10 md:py-20 relative z-10" data-id="element-81">
          <div className="max-w-4xl mx-auto bg-gray-900/80 backdrop-blur-sm rounded-xl p-8 border border-gray-800 luxury-card" data-id="element-82">
            <div className="flex flex-col items-center justify-center py-12" data-id="element-83">
              <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mb-6" data-id="element-84">
                <ShoppingCartIcon className="w-10 h-10 text-gray-600" data-id="element-85" />
              </div>
              <h2 className="text-2xl font-bold mb-4" data-id="element-86">Váš košík je prázdný</h2>
              <p className="text-gray-400 mb-8 text-center" data-id="element-87">
                Přidejte si do košíku nějaké produkty a vraťte se zpět.
              </p>
              <Button variant="primary" className="luxury-button" onClick={handleContinueShopping} data-id="element-88">
                ZPĚT NA HLAVNÍ STRÁNKU
              </Button>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const upsellTotal = upsellChecked ? UPSELL_PRICE : 0;
  const total = subtotal + upsellTotal - discountAmount;
  return <AnimatePresence mode="wait" data-id="element-89">
      {items.length === 0 && !isClearing ? <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} transition={{
      duration: 0.4,
      ease: 'easeInOut'
    }} className="min-h-screen relative bg-black text-white pt-20 overflow-hidden" data-id="element-90">
          <TrainingPlanWarningModal isOpen={trainingPlanWarningOpen} onClose={() => setTrainingPlanWarningOpen(false)} existingPlanName={existingTrainingPlanName || ''} data-id="element-91" />
          {/* Gradient background overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-green-900/40 via-green-900/20 to-black pointer-events-none z-0" data-id="element-92"></div>
          {/* Decorative elements */}
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-green-500/3 rounded-full filter blur-3xl" data-id="element-93"></div>
          <div className="absolute -bottom-10 right-10 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-94"></div>
          <div className="absolute top-1/2 left-1/4 w-40 h-40 bg-green-500/3 rounded-full filter blur-3xl" data-id="element-95"></div>
          <div className="absolute top-1/3 right-1/4 w-32 h-32 bg-green-500/4 rounded-full filter blur-3xl" data-id="element-96"></div>

          <div className="container mx-auto px-4 py-10 md:py-20 relative z-10" data-id="element-97">
            <motion.h1 className="text-2xl md:text-5xl font-black mb-6 md:mb-16 text-center" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-98">
              VÁŠ <span className="text-green-500" data-id="element-99">KOŠÍK</span>
            </motion.h1>
            <div className="flex flex-col lg:flex-row lg:items-stretch gap-4 md:gap-8 max-w-7xl mx-auto" data-id="element-100">
              {/* Cart Items */}
              <motion.div className="lg:w-2/3" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-101">
                <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-gray-800 luxury-card h-full flex flex-col" data-id="element-102">
                  <h2 className="text-lg md:text-xl font-bold mb-4 md:mb-6 flex items-center" data-id="element-103">
                    <ShoppingCartIcon className="w-4 h-4 md:w-5 md:h-5 mr-2 text-green-500" data-id="element-104" />
                    PRODUKTY ({itemCount})
                  </h2>
                  <div className="divide-y divide-gray-800 flex-grow" data-id="element-105">
                    {items.map(item => <div key={item.id} className="py-3 md:py-5 flex flex-col gap-2 md:gap-3" data-id="element-106">
                        <div className="flex items-center gap-3 md:gap-4" data-id="element-107">
                          <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-108">
                            {item.type === 'training' && (item.id.startsWith('training-beginner') ? <img src="https://cdn.magicpatterns.com/uploads/o2XhAUSnkeA3MYurrZTscW/openart-image_uE7NKCUO_1774023938360_raw.jpg" alt="BHS" className="w-full h-full object-cover rounded-lg" data-id="element-109" /> : <div className="w-8 h-8 md:w-10 md:h-10 bg-green-500/20 rounded-full flex items-center justify-center" data-id="element-110">
                                  <DumbbellIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500" data-id="element-111" />
                                </div>)}
                            {item.type === 'greens' && <div className="w-8 h-8 md:w-10 md:h-10 bg-green-500/20 rounded-full flex items-center justify-center" data-id="element-112">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 md:w-5 md:h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-113">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" data-id="element-114" />
                                </svg>
                              </div>}
                            {item.type === 'mushrooms' && <div className="w-8 h-8 md:w-10 md:h-10 bg-green-500/20 rounded-full flex items-center justify-center" data-id="element-115">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 md:w-5 md:h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-116">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" data-id="element-117" />
                                </svg>
                              </div>}
                          </div>
                          <div className="flex-grow min-w-0" data-id="element-118">
                            <h3 className="font-bold text-sm md:text-lg leading-tight" data-id="element-119">
                              {item.name}
                            </h3>
                            {item.details && <p className="text-gray-400 text-xs md:text-sm mt-0.5" data-id="element-120">
                                {item.details}
                              </p>}
                            {couponApplied && item.id === selectedPlanForDiscount && <div className="mt-0.5 text-green-500 text-xs md:text-sm font-medium flex items-center" data-id="element-121">
                                  <CheckIcon className="w-3 h-3 md:w-4 md:h-4 mr-1" data-id="element-122" />
                                  <span data-id="element-123">Sleva 10% (SLEVA10)</span>
                                </div>}
                          </div>
                          <div className="text-right flex-shrink-0" data-id="element-124">
                            <div className="text-base md:text-lg font-bold text-green-500" data-id="element-125">
                              {item.id === selectedPlanForDiscount && couponApplied ? <div data-id="element-126">
                                  <span className="line-through text-gray-400 text-xs mr-1" data-id="element-127">
                                    {item.price} Kč
                                  </span>
                                  <span data-id="element-128">
                                    {Math.round((item.price - discountAmount) * 100) / 100}{' '}
                                    Kč
                                  </span>
                                </div> : `${item.price} Kč`}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3" data-id="element-129">
                          {item.type !== 'training' && <div className="flex items-center bg-gray-800 rounded-lg overflow-hidden" data-id="element-130">
                              <button className="px-2.5 py-1.5 md:px-3 md:py-2 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors" onClick={() => updateQuantity(item.id, item.quantity - 1)} disabled={item.quantity <= 1} data-id="element-131">
                                <MinusIcon className="w-3.5 h-3.5 md:w-4 md:h-4" data-id="element-132" />
                              </button>
                              <div className="px-3 py-1.5 md:px-4 md:py-2 text-center min-w-[36px] text-sm" data-id="element-133">
                                {item.quantity}
                              </div>
                              <button className="px-2.5 py-1.5 md:px-3 md:py-2 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors" onClick={() => updateQuantity(item.id, item.quantity + 1)} data-id="element-134">
                                <PlusIcon className="w-3.5 h-3.5 md:w-4 md:h-4" data-id="element-135" />
                              </button>
                            </div>}
                          <button className="p-1.5 md:p-2 text-gray-400 hover:text-red-500 transition-colors" onClick={() => removeItem(item.id)} data-id="element-136"></button>
                        </div>
                      </div>)}
                  </div>

                  {/* Upsell Checkbox */}
                  {hasBhsPlan && <div className="mt-3 md:mt-5" data-id="element-137">
                      <button onClick={() => setUpsellChecked(!upsellChecked)} className={`w-full flex items-center gap-2.5 md:gap-3 px-3 py-2.5 md:p-4 rounded-lg md:rounded-xl border border-dashed transition-all duration-300 ${upsellChecked ? 'border-green-500/70 bg-green-500/10' : 'border-gray-600/70 bg-gray-800/30 hover:border-gray-500'}`} data-id="element-138">
                        <div className={`w-4 h-4 md:w-5 md:h-5 rounded border-[1.5px] md:border-2 flex items-center justify-center flex-shrink-0 transition-all duration-300 ${upsellChecked ? 'bg-green-500 border-green-500' : 'border-gray-500 bg-transparent'}`} data-id="element-139">
                          {upsellChecked && <CheckIcon className="w-2.5 h-2.5 md:w-3 md:h-3 text-black" strokeWidth={3} data-id="element-140" />}
                        </div>
                        <span className={`text-xs md:text-sm font-medium transition-colors text-left ${upsellChecked ? 'text-green-400' : 'text-gray-300'}`} data-id="element-141">
                          Přidat za 399 Kč ultimátní BHS průvodce výživou
                        </span>
                      </button>
                    </div>}

                  <div className="mt-3 md:mt-6 flex flex-col-reverse md:flex-row justify-between items-center gap-2.5 md:gap-0" data-id="element-142">
                    <button className="text-gray-400 hover:text-red-500 transition-colors flex items-center text-xs md:text-sm" onClick={handleClearCart} data-id="element-143">
                      <TrashIcon className="w-3 h-3 md:w-4 md:h-4 mr-1" data-id="element-144" />
                      <span className="whitespace-nowrap" data-id="element-145">
                        Vyprázdnit košík
                      </span>
                    </button>
                    <Button variant="outline" size="sm" onClick={handleContinueShopping} className="w-full md:w-auto" data-id="element-146">
                      <div className="flex items-center justify-center" data-id="element-147">
                        <ArrowLeftIcon className="w-3 h-3 md:w-4 md:h-4 mr-1" data-id="element-148" />
                        <span className="text-xs md:text-sm whitespace-nowrap" data-id="element-149">
                          POKRAČOVAT V NÁKUPU
                        </span>
                      </div>
                    </Button>
                  </div>
                </div>
              </motion.div>
              {/* Order Summary */}
              <motion.div className="lg:w-1/3" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-150">
                <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-6 border border-gray-800 luxury-card sticky top-24 h-full flex flex-col" data-id="element-151">
                  <h2 className="text-xl font-bold mb-6" data-id="element-152">SOUHRN OBJEDNÁVKY</h2>
                  <div className="space-y-4 mb-6" data-id="element-153">
                    <div className="flex justify-between" data-id="element-154">
                      <span className="text-gray-400" data-id="element-155">Mezisoučet</span>
                      <span className="font-medium" data-id="element-156">{subtotal} Kč</span>
                    </div>
                    {upsellChecked && <div className="flex justify-between" data-id="element-157">
                        <span className="text-gray-400" data-id="element-158">
                          BHS průvodce výživou
                        </span>
                        <span className="font-medium" data-id="element-159">{UPSELL_PRICE} Kč</span>
                      </div>}
                    {couponApplied && discountAmount > 0 && <div className="flex justify-between text-green-500" data-id="element-160">
                        <span data-id="element-161">Sleva (10%)</span>
                        <span data-id="element-162">-{discountAmount.toFixed(1)} Kč</span>
                      </div>}
                    <div className="flex justify-between pt-4 border-t border-gray-800" data-id="element-163">
                      <span className="font-bold" data-id="element-164">CELKEM</span>
                      <span className="font-bold text-xl text-green-500" data-id="element-165">
                        {total.toFixed(0)} Kč
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 text-right" data-id="element-166">
                      (Včetně DPH)
                    </div>
                  </div>
                  <div className="mb-5 flex flex-col items-center justify-center gap-2" data-id="element-167">
                    <span className="text-xs md:text-sm text-gray-300 font-bold tracking-wide" data-id="element-168">
                      Transakci procesuje zabezpečená platební brána
                    </span>
                    <img src="https://cdn.magicpatterns.com/uploads/ecbfMwHALj8LtzTvzNa7qx/aca6e906-d5c5-488c-aeb4-7715ebb6bbdb.png" alt="Stripe" className="h-7 md:h-8" data-id="element-169" />
                  </div>
                  <Button variant="primary" fullWidth className="luxury-button" onClick={handleCheckout} disabled={isRedirecting} style={isRedirecting ? {
                pointerEvents: 'none',
                opacity: 1
              } : {}} data-id="element-170">
                    {isRedirecting ? 'PŘESMĚROVÁVÁM...' : 'POKRAČOVAT K PLATBĚ'}
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
          {/* Redirecting Overlay */}
          <AnimatePresence data-id="element-171">
            {isRedirecting && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center" data-id="element-172">
                <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 0.1
          }} className="text-center" data-id="element-173">
                  <div className="mb-6" data-id="element-174">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full mx-auto" data-id="element-175" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-176">
                    Přesměrovávám na platební bránu...
                  </h3>
                  <p className="text-gray-400" data-id="element-177">
                    Moment prosím, připravujeme vaši objednávku
                  </p>
                </motion.div>
              </motion.div>}
          </AnimatePresence>
        </motion.div> : <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} transition={{
      duration: 0.4,
      ease: 'easeInOut'
    }} className="min-h-screen relative bg-black text-white pt-20 overflow-hidden" data-id="element-178">
          <TrainingPlanWarningModal isOpen={trainingPlanWarningOpen} onClose={() => setTrainingPlanWarningOpen(false)} existingPlanName={existingTrainingPlanName || ''} data-id="element-179" />
          {/* Gradient background overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-green-900/40 via-green-900/20 to-black pointer-events-none z-0" data-id="element-180"></div>
          {/* Decorative elements */}
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-green-500/3 rounded-full filter blur-3xl" data-id="element-181"></div>
          <div className="absolute -bottom-10 right-10 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-182"></div>
          <div className="absolute top-1/2 left-1/4 w-40 h-40 bg-green-500/3 rounded-full filter blur-3xl" data-id="element-183"></div>
          <div className="absolute top-1/3 right-1/4 w-32 h-32 bg-green-500/4 rounded-full filter blur-3xl" data-id="element-184"></div>

          <div className="container mx-auto px-4 py-10 md:py-20 relative z-10" data-id="element-185">
            <motion.h1 className="text-2xl md:text-5xl font-black mb-6 md:mb-16 text-center" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-186">
              VÁŠ <span className="text-green-500" data-id="element-187">KOŠÍK</span>
            </motion.h1>
            <div className="flex flex-col lg:flex-row lg:items-stretch gap-4 md:gap-8 max-w-7xl mx-auto" data-id="element-188">
              {/* Cart Items */}
              <motion.div className="lg:w-2/3" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-189">
                <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-gray-800 luxury-card h-full flex flex-col" data-id="element-190">
                  <h2 className="text-lg md:text-xl font-bold mb-4 md:mb-6 flex items-center" data-id="element-191">
                    <ShoppingCartIcon className="w-4 h-4 md:w-5 md:h-5 mr-2 text-green-500" data-id="element-192" />
                    PRODUKTY ({itemCount})
                  </h2>
                  <div className="divide-y divide-gray-800 flex-grow" data-id="element-193">
                    {items.map(item => <div key={item.id} className="py-3 md:py-5 flex flex-col gap-2 md:gap-3" data-id="element-194">
                        <div className="flex items-center gap-3 md:gap-4" data-id="element-195">
                          <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-196">
                            {item.type === 'training' && (item.id.startsWith('training-beginner') ? <img src="https://cdn.magicpatterns.com/uploads/o2XhAUSnkeA3MYurrZTscW/openart-image_uE7NKCUO_1774023938360_raw.jpg" alt="BHS" className="w-full h-full object-cover rounded-lg" data-id="element-197" /> : <div className="w-8 h-8 md:w-10 md:h-10 bg-green-500/20 rounded-full flex items-center justify-center" data-id="element-198">
                                  <DumbbellIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500" data-id="element-199" />
                                </div>)}
                            {item.type === 'greens' && <div className="w-8 h-8 md:w-10 md:h-10 bg-green-500/20 rounded-full flex items-center justify-center" data-id="element-200">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 md:w-5 md:h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-201">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" data-id="element-202" />
                                </svg>
                              </div>}
                            {item.type === 'mushrooms' && <div className="w-8 h-8 md:w-10 md:h-10 bg-green-500/20 rounded-full flex items-center justify-center" data-id="element-203">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 md:w-5 md:h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-204">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" data-id="element-205" />
                                </svg>
                              </div>}
                          </div>
                          <div className="flex-grow min-w-0" data-id="element-206">
                            <h3 className="font-bold text-sm md:text-lg leading-tight" data-id="element-207">
                              {item.name}
                            </h3>
                            {item.details && <p className="text-gray-400 text-xs md:text-sm mt-0.5" data-id="element-208">
                                {item.details}
                              </p>}
                            {couponApplied && item.id === selectedPlanForDiscount && <div className="mt-0.5 text-green-500 text-xs md:text-sm font-medium flex items-center" data-id="element-209">
                                  <CheckIcon className="w-3 h-3 md:w-4 md:h-4 mr-1" data-id="element-210" />
                                  <span data-id="element-211">Sleva 10% (SLEVA10)</span>
                                </div>}
                          </div>
                          <div className="text-right flex-shrink-0" data-id="element-212">
                            <div className="text-base md:text-lg font-bold text-green-500" data-id="element-213">
                              {item.id === selectedPlanForDiscount && couponApplied ? <div data-id="element-214">
                                  <span className="line-through text-gray-400 text-xs mr-1" data-id="element-215">
                                    {item.price} Kč
                                  </span>
                                  <span data-id="element-216">
                                    {Math.round((item.price - discountAmount) * 100) / 100}{' '}
                                    Kč
                                  </span>
                                </div> : `${item.price} Kč`}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3" data-id="element-217">
                          {item.type !== 'training' && <div className="flex items-center bg-gray-800 rounded-lg overflow-hidden" data-id="element-218">
                              <button className="px-2.5 py-1.5 md:px-3 md:py-2 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors" onClick={() => updateQuantity(item.id, item.quantity - 1)} disabled={item.quantity <= 1} data-id="element-219">
                                <MinusIcon className="w-3.5 h-3.5 md:w-4 md:h-4" data-id="element-220" />
                              </button>
                              <div className="px-3 py-1.5 md:px-4 md:py-2 text-center min-w-[36px] text-sm" data-id="element-221">
                                {item.quantity}
                              </div>
                              <button className="px-2.5 py-1.5 md:px-3 md:py-2 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors" onClick={() => updateQuantity(item.id, item.quantity + 1)} data-id="element-222">
                                <PlusIcon className="w-3.5 h-3.5 md:w-4 md:h-4" data-id="element-223" />
                              </button>
                            </div>}
                          <button className="p-1.5 md:p-2 text-gray-400 hover:text-red-500 transition-colors" onClick={() => removeItem(item.id)} data-id="element-224"></button>
                        </div>
                      </div>)}
                  </div>

                  {/* Upsell Checkbox */}
                  {hasBhsPlan && <div className="mt-3 md:mt-5" data-id="element-225">
                      <button onClick={() => setUpsellChecked(!upsellChecked)} className={`w-full flex items-center gap-2.5 md:gap-3 px-3 py-2.5 md:p-4 rounded-lg md:rounded-xl border border-dashed transition-all duration-300 ${upsellChecked ? 'border-green-500/70 bg-green-500/10' : 'border-gray-600/70 bg-gray-800/30 hover:border-gray-500'}`} data-id="element-226">
                        <div className={`w-4 h-4 md:w-5 md:h-5 rounded border-[1.5px] md:border-2 flex items-center justify-center flex-shrink-0 transition-all duration-300 ${upsellChecked ? 'bg-green-500 border-green-500' : 'border-gray-500 bg-transparent'}`} data-id="element-227">
                          {upsellChecked && <CheckIcon className="w-2.5 h-2.5 md:w-3 md:h-3 text-black" strokeWidth={3} data-id="element-228" />}
                        </div>
                        <span className={`text-xs md:text-sm font-medium transition-colors text-left ${upsellChecked ? 'text-green-400' : 'text-gray-300'}`} data-id="element-229">
                          Přidat za 399 Kč ultimátní BHS průvodce výživou
                        </span>
                      </button>
                    </div>}

                  <div className="mt-3 md:mt-6 flex flex-col-reverse md:flex-row justify-between items-center gap-2.5 md:gap-0" data-id="element-230">
                    <button className="text-gray-400 hover:text-red-500 transition-colors flex items-center text-xs md:text-sm" onClick={handleClearCart} data-id="element-231">
                      <TrashIcon className="w-3 h-3 md:w-4 md:h-4 mr-1" data-id="element-232" />
                      <span className="whitespace-nowrap" data-id="element-233">
                        Vyprázdnit košík
                      </span>
                    </button>
                    <Button variant="outline" size="sm" onClick={handleContinueShopping} className="w-full md:w-auto" data-id="element-234">
                      <div className="flex items-center justify-center" data-id="element-235">
                        <ArrowLeftIcon className="w-3 h-3 md:w-4 md:h-4 mr-1" data-id="element-236" />
                        <span className="text-xs md:text-sm whitespace-nowrap" data-id="element-237">
                          POKRAČOVAT V NÁKUPU
                        </span>
                      </div>
                    </Button>
                  </div>
                </div>
              </motion.div>
              {/* Order Summary */}
              <motion.div className="lg:w-1/3" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-238">
                <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-6 border border-gray-800 luxury-card sticky top-24 h-full flex flex-col" data-id="element-239">
                  <h2 className="text-xl font-bold mb-6" data-id="element-240">SOUHRN OBJEDNÁVKY</h2>
                  <div className="space-y-4 mb-6" data-id="element-241">
                    <div className="flex justify-between" data-id="element-242">
                      <span className="text-gray-400" data-id="element-243">Mezisoučet</span>
                      <span className="font-medium" data-id="element-244">{subtotal} Kč</span>
                    </div>
                    {upsellChecked && <div className="flex justify-between" data-id="element-245">
                        <span className="text-gray-400" data-id="element-246">
                          BHS průvodce výživou
                        </span>
                        <span className="font-medium" data-id="element-247">{UPSELL_PRICE} Kč</span>
                      </div>}
                    {couponApplied && discountAmount > 0 && <div className="flex justify-between text-green-500" data-id="element-248">
                        <span data-id="element-249">Sleva (10%)</span>
                        <span data-id="element-250">-{discountAmount.toFixed(1)} Kč</span>
                      </div>}
                    <div className="flex justify-between pt-4 border-t border-gray-800" data-id="element-251">
                      <span className="font-bold" data-id="element-252">CELKEM</span>
                      <span className="font-bold text-xl text-green-500" data-id="element-253">
                        {total.toFixed(0)} Kč
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 text-right" data-id="element-254">
                      (Včetně DPH)
                    </div>
                  </div>
                  <div className="mb-5 flex flex-col items-center justify-center gap-2" data-id="element-255">
                    <span className="text-xs md:text-sm text-gray-300 font-bold tracking-wide" data-id="element-256">
                      Transakci procesuje zabezpečená platební brána
                    </span>
                    <img src="https://cdn.magicpatterns.com/uploads/ecbfMwHALj8LtzTvzNa7qx/aca6e906-d5c5-488c-aeb4-7715ebb6bbdb.png" alt="Stripe" className="h-7 md:h-8" data-id="element-257" />
                  </div>
                  <Button variant="primary" fullWidth className="luxury-button" onClick={handleCheckout} disabled={isRedirecting} style={isRedirecting ? {
                pointerEvents: 'none',
                opacity: 1
              } : {}} data-id="element-258">
                    {isRedirecting ? 'PŘESMĚROVÁVÁM...' : 'POKRAČOVAT K PLATBĚ'}
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
          {/* Redirecting Overlay */}
          <AnimatePresence data-id="element-259">
            {isRedirecting && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center" data-id="element-260">
                <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 0.1
          }} className="text-center" data-id="element-261">
                  <div className="mb-6" data-id="element-262">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full mx-auto" data-id="element-263" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-264">
                    Přesměrovávám na platební bránu...
                  </h3>
                  <p className="text-gray-400" data-id="element-265">
                    Moment prosím, připravujeme vaši objednávku
                  </p>
                </motion.div>
              </motion.div>}
          </AnimatePresence>
        </motion.div>}
    </AnimatePresence>;
};